import { computed, reactive, toRefs } from 'vue'
import { useStore } from 'vuex'
import { customFilterService } from '../dependency-injection'
import { CustomFilter } from '@b2ag/module-product-sdk'

type CustomFilterState = {
  customFilters: CustomFilter[]
  additionalFilters: string[]
  selectedCrop: string[]
  selectedFilters: Record<string, string[]>
  initialized: boolean
}

const state = reactive<CustomFilterState>({
  customFilters: [],
  additionalFilters: [],
  selectedCrop: [],
  selectedFilters: {},
  initialized: false,
})

export function useCustomFilter() {
  const store = useStore()

  const partnerId = computed(() => store.getters.currentCooperativeId)

  async function fetchCustomFilters() {
    if (!state.initialized) {
      if (partnerId.value) {
        state.customFilters = await customFilterService.getPartnerCustomFilters(partnerId.value)
      } else {
        state.customFilters = await customFilterService.getDefaultFilters()
      }
      state.initialized = true
    }
    return state.customFilters
  }

  function updateAdditionalFiltersByCrop(cropLabel: string | null = null) {
    if (!cropLabel) {
      state.additionalFilters = []
      return
    }

    const matchingFilter = state.customFilters.find((filter) => filter.cropLabel === cropLabel)
    if (matchingFilter && matchingFilter.filters) {
      state.additionalFilters = matchingFilter.filters.sort((a, b) => a.position - b.position).map((f) => f.filter)
    } else {
      state.additionalFilters = []
    }
  }

  function initializeFromAlgoliaParams(paramsString: string) {
    const searchParams = new URLSearchParams(paramsString)
    const facetFilters = searchParams.get('facetFilters')
    if (facetFilters) {
      const parsedFacetFilters = JSON.parse(decodeURIComponent(facetFilters))

      const cropRefinements = extractAttributeFromFacetFilters(parsedFacetFilters, 'crop_oad')
      updateCropState(cropRefinements)

      state.additionalFilters.forEach((filter) => {
        if (!state.selectedFilters[filter]) {
          state.selectedFilters[filter] = []
        }
        state.selectedFilters[filter] = extractAttributeFromFacetFilters(parsedFacetFilters, filter)
      })
    }
  }

  function extractAttributeFromFacetFilters(facetFilters: string[][], attributeName: string) {
    const values: string[] = []

    facetFilters.forEach((filter) => {
      filter.forEach((item) => {
        const [attr, value] = item.split(':')
        if (attr === attributeName && value) {
          values.push(value)
        }
      })
    })

    return values
  }

  const handleCropRefinement = (value: string[], refine: (value: string) => void) => {
    handleCropChange(refine)
    updateCropState(value)
  }

  const handleCropChange = (refine: (value: string) => void) => {
    state.selectedCrop.forEach((val) => {
      refine(val)
    })
  }

  const updateCropState = (newValue: string[]) => {
    state.selectedCrop = newValue
    if (newValue.length > 0) {
      updateAdditionalFiltersByCrop(newValue[0])
    } else {
      updateAdditionalFiltersByCrop(null)
    }
  }

  const handleFilterRefinement = (value: string[], refine: (value: string) => void, filter: string) => {
    if (!state.selectedFilters[filter]) {
      state.selectedFilters[filter] = []
    }

    const currentValues = [...(state.selectedFilters[filter] || [])]
    const added = value.filter((v) => !currentValues.includes(v))
    const removed = currentValues.filter((v) => !value.includes(v))

    added.forEach((val) => refine(val))
    removed.forEach((val) => refine(val))

    state.selectedFilters[filter] = value
  }

  const resetCropFilter = () => {
    state.selectedCrop = []
    updateAdditionalFiltersByCrop(null)
  }

  const resetCustomFilters = () => {
    resetCropFilter()
    Object.keys(state.selectedFilters).forEach((filter) => {
      state.selectedFilters[filter] = []
    })
  }

  const removeFilterValue = (attribute: string, value: string) => {
    if (state.selectedFilters[attribute]) {
      const index = state.selectedFilters[attribute].indexOf(value)
      if (index > -1) {
        state.selectedFilters[attribute].splice(index, 1)
      }
    }
  }

  const stateRefs = toRefs(state)

  return {
    ...stateRefs,

    fetchCustomFilters,
    updateAdditionalFiltersByCrop,
    handleCropRefinement,
    handleFilterRefinement,
    resetCropFilter,
    resetCustomFilters,
    removeFilterValue,
    initializeFromAlgoliaParams,
  }
}
